import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Package, Plus, Minus, AlertTriangle, ArrowUpRight, ArrowDownRight, RefreshCw, Trash2, Boxes } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const Stock = () => {
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showDeleteMovementDialog, setShowDeleteMovementDialog] = useState(false);
  const [showBulkRestockModal, setShowBulkRestockModal] = useState(false);
  const [bulkRestockQuantity, setBulkRestockQuantity] = useState('');
  const [bulkRestockLoading, setBulkRestockLoading] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [movementForm, setMovementForm] = useState({
    type: 'entry',
    quantity: '',
    reason: ''
  });
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prodRes, movRes, lowRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/stock/movements`),
        axios.get(`${API}/stock/low`)
      ]);
      setProducts(prodRes.data.filter(p => p.stock_control));
      setMovements(movRes.data.slice(0, 50));
      setLowStock(lowRes.data);
    } catch (e) {
      toast.error('Erro ao carregar estoque');
    } finally {
      setLoading(false);
    }
  };

  const openMovementModal = (product, type = 'entry') => {
    setSelectedProduct(product);
    setMovementForm({ type, quantity: '', reason: '' });
    setShowMovementModal(true);
  };

  const handleSaveMovement = async () => {
    const qty = parseInt(movementForm.quantity);
    // Para ajuste (contagem), permitir 0. Para entrada/saída, exigir > 0
    if (movementForm.quantity === '' || isNaN(qty)) {
      toast.error('Informe uma quantidade válida');
      return;
    }
    if (movementForm.type !== 'adjustment' && qty <= 0) {
      toast.error('Informe uma quantidade maior que zero');
      return;
    }
    if (movementForm.type === 'adjustment' && qty < 0) {
      toast.error('O novo saldo não pode ser negativo');
      return;
    }

    try {
      await axios.post(`${API}/stock/movement`, {
        product_id: selectedProduct.id,
        type: movementForm.type,
        quantity: parseInt(movementForm.quantity),
        reason: movementForm.reason
      });
      toast.success('Movimentação registrada');
      setShowMovementModal(false);
      fetchData();
    } catch (e) {
      toast.error('Erro ao registrar movimentação');
    }
  };

  const handleDeleteMovement = async () => {
    if (!selectedMovement) return;
    try {
      await axios.delete(`${API}/stock/movement/${selectedMovement.id}`);
      toast.success('Movimentação excluída! O estoque foi revertido.');
      setShowDeleteMovementDialog(false);
      setSelectedMovement(null);
      fetchData();
    } catch (e) {
      toast.error('Erro ao excluir movimentação');
    }
  };

  const handleBulkRestock = async () => {
    const qty = parseInt(bulkRestockQuantity);
    if (!qty || qty <= 0) {
      toast.error('Informe uma quantidade válida');
      return;
    }
    
    setBulkRestockLoading(true);
    try {
      const res = await axios.post(`${API}/stock/bulk-restock`, { quantity: qty });
      toast.success(`${res.data.products_updated} produtos atualizados para ${qty} unidades!`);
      setShowBulkRestockModal(false);
      setBulkRestockQuantity('');
      fetchData();
    } catch (e) {
      console.error('Bulk restock error:', e);
      const errorMsg = e.response?.data?.detail || 'Erro ao abastecer estoque. Tente novamente.';
      toast.error(errorMsg);
    } finally {
      setBulkRestockLoading(false);
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout title="Estoque">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Estoque">
      <div className="space-y-6 pb-20 lg:pb-0">
        {/* Low Stock Alert */}
        {lowStock.length > 0 && (
          <Card className="card-adegga border-yellow-500/30 bg-yellow-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                Produtos com Estoque Baixo ({lowStock.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {lowStock.map(p => (
                  <Badge
                    key={p.id}
                    className={`cursor-pointer ${p.stock_quantity <= 0 ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}
                    onClick={() => openMovementModal(p, 'entry')}
                  >
                    {p.name}: {p.stock_quantity} un
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Products List */}
          <Card className="card-adegga lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-white">Produtos ({filteredProducts.length})</CardTitle>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={() => setShowBulkRestockModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Boxes className="w-4 h-4 mr-1" />
                  Abastecer Todos
                </Button>
                <Input
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-40 h-8 bg-[#0f0f15] border-white/10 text-white"
                />
                <Button size="sm" variant="ghost" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-white/60">Produto</TableHead>
                    <TableHead className="text-white/60 text-center">Estoque</TableHead>
                    <TableHead className="text-white/60 text-center">Mínimo</TableHead>
                    <TableHead className="text-white/60 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map(product => (
                    <TableRow key={product.id} className="border-white/5">
                      <TableCell className="text-white">{product.name}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={
                          product.stock_quantity <= 0
                            ? 'bg-red-500/20 text-red-400'
                            : product.stock_quantity <= product.stock_alert_min
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-green-500/20 text-green-400'
                        }>
                          {product.stock_quantity}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-white/60">{product.stock_alert_min}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            onClick={() => openMovementModal(product, 'entry')}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => openMovementModal(product, 'exit')}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            onClick={() => openMovementModal(product, 'adjustment')}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredProducts.length === 0 && (
                <div className="text-center py-8 text-white/40">
                  Nenhum produto com controle de estoque
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Movements */}
          <Card className="card-adegga">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base">Movimentações Recentes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
              {movements.map(mov => (
                <div key={mov.id} className="flex items-center gap-3 p-2 bg-[#0f0f15] rounded-lg group">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    mov.type === 'entry' ? 'bg-green-500/20' :
                    mov.type === 'exit' ? 'bg-red-500/20' : 'bg-blue-500/20'
                  }`}>
                    {mov.type === 'entry' ? (
                      <ArrowUpRight className="w-4 h-4 text-green-400" />
                    ) : mov.type === 'exit' ? (
                      <ArrowDownRight className="w-4 h-4 text-red-400" />
                    ) : (
                      <RefreshCw className="w-4 h-4 text-blue-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{mov.product_name}</p>
                    <p className="text-xs text-white/40">{mov.reason || '-'}</p>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <div>
                      <span className={`font-bold ${
                        mov.type === 'entry' ? 'text-green-400' :
                        mov.type === 'exit' ? 'text-red-400' : 'text-blue-400'
                      }`}>
                        {mov.type === 'entry' ? '+' : mov.type === 'exit' ? '-' : '='}{mov.quantity}
                      </span>
                      <p className="text-xs text-white/40">
                        {new Date(mov.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-opacity"
                      onClick={() => {
                        setSelectedMovement(mov);
                        setShowDeleteMovementDialog(true);
                      }}
                      data-testid={`delete-movement-${mov.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
              {movements.length === 0 && (
                <p className="text-center text-white/40 py-4">Nenhuma movimentação</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Movement Modal */}
        <Dialog open={showMovementModal} onOpenChange={setShowMovementModal}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">
                {movementForm.type === 'entry' ? 'Entrada de Estoque' :
                 movementForm.type === 'exit' ? 'Saída de Estoque' : 'Ajuste de Estoque'}
              </DialogTitle>
            </DialogHeader>
            {selectedProduct && (
              <div className="space-y-4">
                <div className="bg-[#0f0f15] p-4 rounded-lg">
                  <p className="font-medium text-white">{selectedProduct.name}</p>
                  <p className="text-sm text-white/60">Estoque atual: {selectedProduct.stock_quantity}</p>
                </div>
                
                <Select
                  value={movementForm.type}
                  onValueChange={(v) => setMovementForm({ ...movementForm, type: v })}
                >
                  <SelectTrigger className="bg-[#0f0f15] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10">
                    <SelectItem value="entry">Entrada (Compra)</SelectItem>
                    <SelectItem value="exit">Saída (Manual)</SelectItem>
                    <SelectItem value="adjustment">Ajuste (Contagem)</SelectItem>
                  </SelectContent>
                </Select>
                
                <div>
                  <label className="text-sm text-white/60 mb-1 block">
                    {movementForm.type === 'adjustment' ? 'Novo saldo' : 'Quantidade'}
                  </label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={movementForm.quantity}
                    onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                </div>
                
                <Textarea
                  placeholder="Motivo (opcional)"
                  value={movementForm.reason}
                  onChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })}
                  className="bg-[#0f0f15] border-white/10 text-white"
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowMovementModal(false)}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleSaveMovement}>
                Confirmar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Movement Dialog */}
        <Dialog open={showDeleteMovementDialog} onOpenChange={setShowDeleteMovementDialog}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Excluir Movimentação</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-white/60">
                Tem certeza que deseja excluir esta movimentação de <strong className="text-white">{selectedMovement?.product_name}</strong>?
              </p>
              <p className="text-sm text-yellow-400 mt-2">
                O estoque será revertido automaticamente ({selectedMovement?.type === 'entry' ? 'entrada removida' : selectedMovement?.type === 'exit' ? 'saída cancelada' : 'ajuste desfeito'}).
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowDeleteMovementDialog(false)}>Cancelar</Button>
              <Button 
                className="bg-red-600 hover:bg-red-700" 
                onClick={handleDeleteMovement}
                data-testid="confirm-delete-movement"
              >
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Restock Modal */}
        <Dialog open={showBulkRestockModal} onOpenChange={setShowBulkRestockModal}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Boxes className="w-5 h-5 text-green-400" />
                Abastecer Todo o Estoque
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <p className="text-white/60">
                Defina a quantidade de estoque para <strong className="text-white">TODOS os {products.length} produtos</strong> de uma vez.
              </p>
              
              <div>
                <label className="text-sm text-white/60 mb-2 block">Nova quantidade para todos os produtos:</label>
                <Input
                  type="number"
                  placeholder="Ex: 100, 500, 1000..."
                  value={bulkRestockQuantity}
                  onChange={(e) => setBulkRestockQuantity(e.target.value)}
                  className="bg-[#0f0f15] border-white/10 text-white text-lg h-12"
                  autoFocus
                />
              </div>

              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-yellow-400 text-sm">
                  ⚠️ Isso irá <strong>substituir</strong> o estoque atual de todos os produtos pela nova quantidade.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowBulkRestockModal(false)}>Cancelar</Button>
              <Button 
                className="bg-green-600 hover:bg-green-700" 
                onClick={handleBulkRestock}
                disabled={bulkRestockLoading || !bulkRestockQuantity}
              >
                {bulkRestockLoading ? 'Atualizando...' : `Abastecer ${products.length} Produtos`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Stock;
